import React, {createContext, useContext, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import moment from 'moment';
import Configuration from '../config';
import './calendar-hls-availability.css';

export const HlsAvailabilityContext = createContext({intervals: []});

export function intervalSlices(intervals, start, end) {
  return intervals.filter(i => i.start < end && i.end > start).map(i => ({
    start: Math.max(i.start, start), end: Math.min(i.end, end),
    top: 100 * (Math.max(i.start, start) - start) / (end - start),
    height: 100 * (Math.min(i.end, end) - Math.max(i.start, start)) / (end - start)
  }));
}

export function ArchiveTimeSlot({value, resource, children}) {
  const availability = useContext(HlsAvailabilityContext);
  const intervals = resource == null ? availability.intervals
    : (availability.fieldIntervals || {})[resource] || [];
  const start = value.getTime() / 1000;
  const slices = intervalSlices(intervals, start, start + 15 * 60);
  if (!slices.length) return children;
  return React.cloneElement(React.Children.only(children), {
    style: {...children.props.style, position: 'relative'}
  }, children.props.children, slices.map((slice, index) => <span key={index}
    className="hls-archive-strip" aria-hidden="true"
    style={{top: `${slice.top}%`, height: `${slice.height}%`}}/>));
}

export function CalendarHlsAvailability({fieldId, fieldName, date, view = 'week', locale = 'it', token, children}) {
  const parent = useContext(HlsAvailabilityContext);
  const [catalog, setCatalog] = useState(null), [selected, setSelected] = useState('');
  const [error, setError] = useState(false), [clock, setClock] = useState(Date.now());
  const rangeStart = moment(date).locale(locale === 'en' ? 'en-gb' : locale).startOf(view === 'day' ? 'day' : 'week');
  const start = rangeStart.toISOString();
  const end = rangeStart.clone().add(view === 'day' ? 1 : 7, 'days').toISOString();
  useEffect(() => {
    if (!fieldId || !token) return undefined;
    let active = true, timer, expiry, requestTimeout;
    let abort;
    setCatalog(null); setError(false);
    const load = async () => {
      clearTimeout(timer); clearTimeout(requestTimeout);
      if (abort) abort.abort();
      abort = new AbortController();
      const currentAbort = abort;
      const requestedAt = Date.now();
      requestTimeout = setTimeout(() => {
        if (active && abort === currentAbort) {
          currentAbort.abort(); setCatalog(null); setError(true);
          timer = setTimeout(load, 30000);
        }
      }, 15000);
      try {
        const response = await fetch(`${Configuration.apiEndPoint}/partners/fields/${fieldId}/hls-availability?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`, {
          headers: {Authorization: `Bearer ${token}`}, cache: 'no-store', signal: currentAbort.signal
        });
        if (!response.ok) throw new Error('unavailable');
        const data = await response.json();
        if (!active || currentAbort.signal.aborted) return;
        const received = Date.now();
        data.scope = `${fieldId}|${start}|${end}`;
        data.cameras = data.cameras.map(c => ({...c, expiresAt: requestedAt + Math.max(0, Math.min(120, c.validForSeconds || 0)) * 1000}));
        setCatalog(data); setError(false); setClock(received);
        setSelected(previous => data.cameras.some(c => c.cameraKey === previous) ? previous : (data.cameras[0] || {}).cameraKey || '');
        const expirations = data.cameras.map(c => c.expiresAt).filter(t => t > received);
        clearTimeout(expiry);
        if (expirations.length) expiry = setTimeout(() => {
          // Clear all evidence conservatively even if polling is slow/stalled.
          if (active) {setCatalog(null); setClock(Date.now());}
        }, Math.min(...expirations) - received);
      } catch (e) {
        if (active && !currentAbort.signal.aborted) {setCatalog(null); setError(true);}
      } finally {
        if (abort === currentAbort) clearTimeout(requestTimeout);
        if (active && !currentAbort.signal.aborted) timer = setTimeout(load, 30000);
      }
    };
    const visible = () => {if (!document.hidden) load();};
    load();
    document.addEventListener('visibilitychange', visible);
    return () => {active = false; clearTimeout(timer); clearTimeout(expiry); clearTimeout(requestTimeout); if (abort) abort.abort(); document.removeEventListener('visibilitychange', visible);};
  }, [fieldId, token, start, end]);
  const camera = catalog && catalog.cameras.find(c => c.cameraKey === selected);
  const usable = camera && catalog.online && catalog.scope === `${fieldId}|${start}|${end}`
    && camera.status === 'available' && camera.expiresAt > clock;
  const intervals = usable ? camera.intervals : [];
  return <HlsAvailabilityContext.Provider value={{intervals, fieldIntervals: {...parent.fieldIntervals, [fieldId]: intervals}}}>
    <div className="hls-availability-legend" aria-label="Disponibilità archivio NVR">
      <strong>Archivio NVR{fieldName ? ` · ${fieldName}` : ''}</strong>{' '}
      {catalog && catalog.cameras.length > 0 && <select aria-label="Camera archivio NVR" value={selected} onChange={e => setSelected(e.target.value)}>
        {catalog.cameras.map(c => <option key={c.cameraKey} value={c.cameraKey}>{c.name} ({c.backendCameraIds.join(', ')})</option>)}
      </select>}
      {' '}{intervals.length > 0 ? <span><span className="hls-availability-swatch"/> Fasce registrate disponibili sulla camera selezionata.</span>
        : error ? 'Disponibilità non verificabile: aggiornamento non riuscito.'
        : !catalog ? 'Disponibilità in aggiornamento.'
        : !catalog.cameras.length ? 'Nessuna sorgente HLS configurata.'
        : !usable ? 'Disponibilità non confermata: NVR offline, inventario assente o non aggiornato.'
        : 'Nessuna registrazione disponibile nel periodo visualizzato.'}
      <div className="small">La striscia indica video presente, non orari programmati. La disponibilità viene ricontrollata quando si richiede l’evento.</div>
    </div>
    {children}
  </HlsAvailabilityContext.Provider>;
}

export default connect(({login: {token}}) => ({token}))(CalendarHlsAvailability);

export function FieldsHlsAvailability({fields, children, ...props}) {
  return fields.reduceRight((content, field) => <CalendarHlsAvailability key={field.id}
    {...props} fieldId={field.id} fieldName={field.name}>{content}</CalendarHlsAvailability>, children);
}

export const CalendarFieldsHlsAvailability = connect(({login: {token}}) => ({token}))(FieldsHlsAvailability);
